import { ca } from 'date-fns/locale';
import { promises } from 'dns';
import { get } from 'jquery';
import { defineStore } from 'pinia';
import { QForm, is } from 'quasar';
import type { dynamicObj } from 'src/pages/assets';
import { docXsltApiService } from 'src/services';

export const useDocXsltStore = defineStore('doc_xslt', {
  state: () => ({
    current_model: {
      draft_id: '',
      attribute_id: '',
      is_active: '',
      is_draft: '',
      doc_type: '',
      e_doc_type: '',
      xslt_data: '',
      attribute_name: '',
      attribute_type: '',
      doc_type_key: '',
    } as dynamicObj,
    params: {
      doc_type: '',
      attribute_id: '',
      include_values: false,
    } as dynamicObj,
    processing: false,
    e_doc_data: {} as dynamicObj,
    versions: {} as dynamicObj,
    confirmDeleteDialog: false,
    formRef: null as QForm | null,
    isReadOnly: false,
    showTestingPanel: false,
  }),
  actions: {
    setCurrentModel(model: dynamicObj) {
      this.current_model = model;
    },
    orderDataByAttributeName(data: any[]): any[] {
      const order: { [key: string]: number } = {
        STRUCTURE_DESCRIPTOR: 0,
        TRANSFORMATION: 1,
        PREVIEW: 2,
        EXTRACTION_PATHS: 3,
      };
      data.sort((a: any, b: any) => {
        return order[a.attribute_name] - order[b.attribute_name];
      });
      return data;
    },
    resetCurrentModel() {
      this.current_model = {
        draft_id: '',
        attribute_id: '',
        is_active: '',
        is_draft: '',
        doc_type: '',
        e_doc_type: '',
        xslt_data: '',
        attribute_name: '',
        attribute_type: '',
        doc_type_key: '',
      };
    },
    async getDocXslt() {
      const formData = new FormData();
      formData.append('doc_type_key', this.params.doc_type);
      formData.append('include_values', this.params.include_values);

      const response = await docXsltApiService.getByEDocType(formData);

      for (const [key, item] of Object.entries(response.data)) {
        const keyStr = key as any;
        this.e_doc_data[keyStr] = this.orderDataByAttributeName(item as any);
      }
    },
    async listVersion(params: string) {
      const data = {
        doc_type_key: this.current_model.doc_type,
        electronic_document_type: this.current_model.e_doc_type,
        include_values: true,
        attribute_id: params ? params : this.params.attribute_id,
      };
      const formData = new FormData();
      for (const [key, value] of Object.entries(data)) {
        formData.append(key, String(value));
      }
      const response = await docXsltApiService.listVersion(formData);
      this.versions = response.data;
      if (this.versions.length > 0) {
        this.current_model.xslt_data = this.versions[0].value;
        this.current_model.draft_id = this.versions[0].id;
        this.current_model.is_active = this.versions[0].is_active;
        this.current_model.is_draft = this.versions[0].is_draft;
      }
    },
    async createExtractionAttributeValuesDraft(): Promise<void> {
      try {
        const data = {
          attribute_id: this.current_model.attribute_id,
          is_active: this.current_model.is_active,
          attribute_value: this.current_model.xslt_data,
        };
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
          formData.append(key, String(value));
        }
        const response =
          await docXsltApiService.createExtractionAttributeValuesDraft(
            formData,
          );
      } catch (error) {
        throw error;
      }
    },
    async activateExtractionAttributeValueDraft(): Promise<void> {
      try {
        const data = {
          attribute_id: this.current_model.attribute_id,
          draft_id: this.current_model.draft_id,
        };
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
          formData.append(key, String(value));
        }
        const response =
          await docXsltApiService.activateExtractionAttributeValueDraft(
            formData,
          );
      } catch (error) {
        throw error;
      }
    },
    async updateExtractionAttributeValueDraft(): Promise<void> {
      try {
        const data = {
          attribute_id: this.current_model.attribute_id,
          draft_id: this.current_model.draft_id,
          value: this.current_model.xslt_data,
        };
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
          formData.append(key, String(value));
        }
        const response =
          await docXsltApiService.updateExtractionAttributeValueDraft(formData);
      } catch (error) {
        throw error;
      }
    },
    async deleteExtractionAttributeValueDrafts(): Promise<void> {
      try {
        const data = {
          attribute_id: this.current_model.attribute_id,
          draft_id: this.current_model.draft_id,
        };
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
          formData.append(key, String(value));
        }
        const response =
          await docXsltApiService.deleteExtractionAttributeValueDraft(formData);
      } catch (error) {
        throw error;
      }
    },
    async createExtractionAttribute(): Promise<any> {
      const data = {
        attribute_name: this.current_model.attribute_name,
        attribute_type: this.current_model.attribute_type,
        doc_type_key: this.current_model.doc_type_key,
        electronic_document_type: this.current_model.e_doc_type,
        attribute_value: this.current_model.xslt_data,
      };
      const formData = new FormData();
      for (const [key, value] of Object.entries(data)) {
        formData.append(key, String(value));
      }
      return docXsltApiService.createExtractionAttribute(formData);
    },
    async deleteExtractionAttribute(): Promise<void> {
      try {
        const data = {
          attribute_id: this.current_model.attribute_id,
        };
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
          formData.append(key, String(value));
        }
        const response =
          await docXsltApiService.deleleExtractionAttribute(formData);
      } catch (error) {
        throw error;
      }
    },
    async createExtractionAttributeValuesDraftFrom(): Promise<void> {
      try {
        const data = {
          attribute_id: this.current_model.attribute_id,
          draft_id: this.current_model.draft_id,
        };
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
          formData.append(key, String(value));
        }
        const response =
          await docXsltApiService.createExtractionAttributeValuesDraftFrom(
            formData,
          );
      } catch (error) {
        throw error;
      }
    },
  },
});
